import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SessionHistoryTable = ({ 
  sessions, 
  onEdit, 
  onDelete,
  onDeleteAll,
  onExport, 
  onImport, 
  isLoading = false,
  isImporting = false,
  readinessMap = {}
}) => {
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);

  const getRPEColor = (rpe) => {
    if (rpe <= 3) return 'text-success bg-success/10';
    if (rpe <= 6) return 'text-warning bg-warning/10';
    return 'text-error bg-error/10';
  };

  // NEW: Get readiness color based on thresholds
  const getReadinessColor = (readiness) => {
    if (readiness < 0.33) return 'text-success bg-success/10'; // Green - Good
    if (readiness < 0.66) return 'text-warning bg-warning/10'; // Amber - Caution
    return 'text-error bg-error/10'; // Red - High Risk
  };

  // NEW: Get readiness label
  const getReadinessLabel = (readiness) => {
    if (readiness < 0.33) return 'Good';
    if (readiness < 0.66) return 'Caution';
    return 'High Risk';
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // NEW: Format activity type for display
  const formatActivityType = (activityType) => {
    if (!activityType) return '—';
    // Capitalize first letter of each word
    return activityType?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortedSessions = () => {
    if (!sessions) return [];
    
    return [...sessions]?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];
      
      if (sortBy === 'date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedSessions?.length === sessions?.length) {
      setSelectedSessions([]);
    } else {
      setSelectedSessions(sessions?.map(s => s?.id));
    }
  };

  const handleSelectSession = (sessionId) => {
    if (selectedSessions?.includes(sessionId)) {
      setSelectedSessions(selectedSessions?.filter(id => id !== sessionId));
    } else {
      setSelectedSessions([...selectedSessions, sessionId]);
    }
  };

  const handleFileUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      onImport(file);
      event.target.value = ''; // Reset file input
    }
  };

  const sortedSessions = getSortedSessions();

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      {/* Header with title and controls */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Session History</h3>
            <p className="text-sm text-text-secondary">
              {sessions?.length || 0} total sessions
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Icon name="Database" size={20} color="var(--color-text-secondary)" strokeWidth={2} />
          </div>
        </div>

        {/* Import/Export Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={onExport}
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed smooth-transition"
            >
              {isLoading ? (
                <Icon name="Loader" size={16} color="currentColor" strokeWidth={2} className="animate-spin" />
              ) : (
                <Icon name="Download" size={16} color="currentColor" strokeWidth={2} />
              )}
              <span>Export CSV</span>
            </button>

            <label className="flex items-center space-x-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 cursor-pointer smooth-transition">
              {isImporting ? (
                <Icon name="Loader" size={16} color="currentColor" strokeWidth={2} className="animate-spin" />
              ) : (
                <Icon name="Upload" size={16} color="currentColor" strokeWidth={2} />
              )}
              <span>Import CSV</span>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                disabled={isImporting}
                className="hidden"
              />
            </label>

            <button
              onClick={() => setShowDeleteAllConfirm(true)}
              disabled={isLoading || sessions?.length === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-error text-error-foreground rounded-lg hover:bg-error/90 disabled:opacity-50 disabled:cursor-not-allowed smooth-transition"
            >
              <Icon name="Trash2" size={16} color="currentColor" strokeWidth={2} />
              <span>Delete All</span>
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="p-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedSessions?.length === sessions?.length && sessions?.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th 
                className="p-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wide cursor-pointer hover:text-text-primary smooth-transition"
                onClick={() => handleSort('date')}
              >
                <div className="flex items-center space-x-1">
                  <span>Date</span>
                  {sortBy === 'date' && (
                    <Icon 
                      name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                      color="currentColor" 
                      strokeWidth={2} 
                    />
                  )}
                </div>
              </th>
              {/* NEW: Activity Type Column */}
              <th 
                className="p-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wide cursor-pointer hover:text-text-primary smooth-transition"
                onClick={() => handleSort('activityType')}
              >
                <div className="flex items-center space-x-1">
                  <span>Activity</span>
                  {sortBy === 'activityType' && (
                    <Icon 
                      name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                      color="currentColor" 
                      strokeWidth={2} 
                    />
                  )}
                </div>
              </th>
              <th 
                className="p-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wide cursor-pointer hover:text-text-primary smooth-transition"
                onClick={() => handleSort('minutes')}
              >
                <div className="flex items-center space-x-1">
                  <span>Total Time</span>
                  {sortBy === 'minutes' && (
                    <Icon 
                      name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                      color="currentColor" 
                      strokeWidth={2} 
                    />
                  )}
                </div>
              </th>
              {/* NEW: Active Time Column */}
              <th 
                className="p-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wide cursor-pointer hover:text-text-primary smooth-transition"
                onClick={() => handleSort('activeTime')}
              >
                <div className="flex items-center space-x-1">
                  <span>Active Time</span>
                  {sortBy === 'activeTime' && (
                    <Icon 
                      name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                      color="currentColor" 
                      strokeWidth={2} 
                    />
                  )}
                </div>
              </th>
              <th 
                className="p-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wide cursor-pointer hover:text-text-primary smooth-transition"
                onClick={() => handleSort('rpe')}
              >
                <div className="flex items-center space-x-1">
                  <span>RPE</span>
                  {sortBy === 'rpe' && (
                    <Icon 
                      name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                      color="currentColor" 
                      strokeWidth={2} 
                    />
                  )}
                </div>
              </th>
              <th 
                className="p-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wide cursor-pointer hover:text-text-primary smooth-transition"
                onClick={() => handleSort('load')}
              >
                <div className="flex items-center space-x-1">
                  <span>Load</span>
                  {sortBy === 'load' && (
                    <Icon 
                      name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={14} 
                      color="currentColor" 
                      strokeWidth={2} 
                    />
                  )}
                </div>
              </th>
              {/* NEW: Readiness Column */}
              <th className="p-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wide">
                Readiness
              </th>
              <th className="p-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wide">
                Notes
              </th>
              <th className="p-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedSessions?.length === 0 ? (
              <tr>
                <td colSpan="10" className="p-8 text-center text-text-secondary">
                  <div className="flex flex-col items-center space-y-2">
                    <Icon name="Calendar" size={32} color="var(--color-text-secondary)" strokeWidth={1.5} />
                    <p>No training sessions found</p>
                    <p className="text-xs">Import CSV data or log your first session</p>
                  </div>
                </td>
              </tr>
            ) : (
              sortedSessions?.map((session) => (
                <tr 
                  key={session?.id}
                  className="hover:bg-muted/50 smooth-transition"
                >
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selectedSessions?.includes(session?.id)}
                      onChange={() => handleSelectSession(session?.id)}
                      className="rounded border-border"
                    />
                  </td>
                  <td className="p-3">
                    <div className="text-sm font-medium text-text-primary">
                      {formatDate(session?.date)}
                    </div>
                  </td>
                  {/* NEW: Activity Type Display */}
                  <td className="p-3">
                    <div className="flex items-center space-x-1">
                      <Icon name="Activity" size={14} color="var(--color-text-secondary)" strokeWidth={2} />
                      <span className="text-sm text-text-secondary">
                        {formatActivityType(session?.activityType)}
                      </span>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center space-x-1 text-sm text-text-secondary">
                      <Icon name="Clock" size={14} color="currentColor" strokeWidth={2} />
                      <span>{formatDuration(session?.minutes)}</span>
                    </div>
                  </td>
                  {/* NEW: Active Time Display */}
                  <td className="p-3">
                    <div className="flex items-center space-x-1 text-sm font-medium text-primary">
                      <Icon name="Zap" size={14} color="currentColor" strokeWidth={2} />
                      <span>{formatDuration(session?.activeTime || session?.minutes)}</span>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getRPEColor(session?.rpe)}`}>
                      {session?.rpe}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="text-sm font-medium text-text-primary">
                      {session?.load}
                      <span className="text-xs text-text-secondary ml-1">AU</span>
                    </div>
                  </td>
                  {/* NEW: Readiness Display */}
                  <td className="p-3">
                    {(() => {
                      const readinessValue = readinessMap?.[session?.date];
                      if (readinessValue === null || readinessValue === undefined) {
                        return (
                          <span className="text-sm text-text-secondary">—</span>
                        );
                      }
                      return (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-text-primary">
                            {readinessValue?.toFixed(2)}
                          </span>
                          <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getReadinessColor(readinessValue)}`}>
                            {getReadinessLabel(readinessValue)}
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="p-3 max-w-xs">
                    <div className="text-sm text-text-secondary truncate" title={session?.notes}>
                      {session?.notes || '—'}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => onEdit(session)}
                        className="p-1 text-text-secondary hover:text-primary smooth-transition"
                        title="Edit session"
                      >
                        <Icon name="Edit" size={16} color="currentColor" strokeWidth={2} />
                      </button>
                      <button
                        onClick={() => onDelete(session?.id)}
                        className="p-1 text-text-secondary hover:text-error smooth-transition"
                        title="Delete session"
                      >
                        <Icon name="Trash2" size={16} color="currentColor" strokeWidth={2} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer with summary */}
      {sessions?.length > 0 && (
        <div className="p-4 bg-muted/30 border-t border-border">
          <div className="flex items-center justify-between text-sm text-text-secondary">
            <div className="flex items-center space-x-4">
              <span>Total Sessions: {sessions?.length}</span>
              <span>•</span>
              <span>Total Load: {sessions?.reduce((sum, s) => sum + s?.load, 0)} AU</span>
              <span>•</span>
              <span>Avg RPE: {(sessions?.reduce((sum, s) => sum + s?.rpe, 0) / sessions?.length)?.toFixed(1)}</span>
              {/* NEW: Add average active time to summary */}
              <span>•</span>
              <span>Avg Active: {formatDuration(Math.round(sessions?.reduce((sum, s) => sum + (s?.activeTime || s?.minutes), 0) / sessions?.length))}</span>
            </div>
            {selectedSessions?.length > 0 && (
              <div className="text-primary">
                {selectedSessions?.length} selected
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete All Confirmation Dialog */}
      {showDeleteAllConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
                  <Icon name="AlertTriangle" size={24} color="var(--color-error)" strokeWidth={2} />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-text-primary mb-2">Delete All Sessions?</h3>
                <p className="text-sm text-text-secondary mb-4">
                  This will permanently delete all {sessions?.length} training sessions. This action cannot be undone.
                </p>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteAllConfirm(false)}
                    className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary smooth-transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowDeleteAllConfirm(false);
                      onDeleteAll();
                    }}
                    className="px-4 py-2 text-sm font-medium bg-error text-error-foreground rounded-lg hover:bg-error/90 smooth-transition"
                  >
                    Delete All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionHistoryTable;